export const WEEKDAY_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const DAY_GROUP_ORDER = { Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6, Sunday: 7, Online: 8, Other: 9 };

export const parseWeekdays = (s) => {
  if (!s) return [];
  const map = { M: 'Monday', T: 'Tuesday', W: 'Wednesday', Th: 'Thursday', F: 'Friday', Sa: 'Saturday', Su: 'Sunday' };
  const days = [];
  let i = 0;
  s = s.trim();
  while (i < s.length) {
    const two = s.substring(i, i + 2);
    if (map[two]) { days.push(map[two]); i += 2; continue; }
    const one = s[i];
    if (map[one] && one !== ' ') days.push(map[one]);
    i += 1;
  }
  return days;
};

export const formatTime12 = (t) => {
  if (!t) return 'TBA';
  const m = String(t).match(/(\d{1,2}):(\d{2})/);
  if (!m) return 'TBA';
  let h = parseInt(m[1], 10);
  const mm = m[2];
  const suffix = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${mm} ${suffix}`;
};

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const formatShortDate = (d) => `${SHORT_MONTHS[d.getMonth()]} ${d.getDate()}`;

export const formatLocation = (building, room) => {
  const b = building || '';
  const r = room || '';
  if (!b && !r) return 'Location TBA';
  if (!b) return r;
  if (!r) return b;
  return `${b} ${r}`;
};

export const parseMinutes = (t) => {
  if (!t) return null;
  const m = String(t).match(/(\d{1,2}):(\d{2})/);
  if (!m) return null;
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
};

export const toLocalDate = (ymd) => {
  if (!ymd) return null;
  const m = String(ymd).match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const meetingDayLabels = (meeting) => {
  const labels = parseWeekdays(meeting.weekdays);
  if (labels.length) return labels;
  if (meeting.instruction_method === 'ONLN' || meeting.instruction_desc === 'On-Line') return ['Online'];
  return ['Other'];
};

const sortByStartTime = (a, b) => (parseMinutes(a.start_time) ?? 0) - (parseMinutes(b.start_time) ?? 0);

export const groupByWeekday = (meetings) => {
  const days = {};
  meetings.forEach((meeting) => {
    meetingDayLabels(meeting).forEach((day) => {
      if (!days[day]) days[day] = [];
      days[day].push(meeting);
    });
  });
  return Object.keys(days)
    .sort((a, b) => (DAY_GROUP_ORDER[a] || 99) - (DAY_GROUP_ORDER[b] || 99))
    .map((day) => ({ day, items: [...days[day]].sort(sortByStartTime) }));
};

export const getDayMeetings = (meetings, date) => {
  const target = startOfDay(date);
  const dayName = WEEKDAY_ORDER[(target.getDay() + 6) % 7];
  return meetings
    .filter((meeting) => {
      if (!parseWeekdays(meeting.weekdays).includes(dayName)) return false;
      const start = toLocalDate(meeting.start_date);
      const end = toLocalDate(meeting.end_date);
      if (start && target < start) return false;
      if (end && target > end) return false;
      return true;
    })
    .sort(sortByStartTime);
};

const labelForOffset = (offset, date) => {
  if (offset === 0) return 'Today';
  if (offset === 1) return 'Tomorrow';
  return date.toLocaleDateString('en-US', { weekday: 'long' });
};

export const resolveActiveDay = (meetings, now = new Date()) => {
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  for (let offset = 0; offset <= 13; offset += 1) {
    const date = startOfDay(now);
    date.setDate(date.getDate() + offset);
    const dayMeetings = getDayMeetings(meetings, date);
    if (!dayMeetings.length) continue;
    if (offset === 0) {
      const lastEnd = Math.max(...dayMeetings.map((m) => parseMinutes(m.end_time) ?? 0));
      if (nowMinutes >= lastEnd) continue;
    }
    return { date, label: labelForOffset(offset, date), meetings: dayMeetings, isToday: offset === 0 };
  }
  return null;
};

const meetingSignature = (m) => [m.weekdays, m.start_time, m.end_time, m.building, m.room, m.start_date, m.end_date, m.instruction_method].join('|');

export const normalizeCourses = (rawCourses) => {
  if (!Array.isArray(rawCourses)) return [];
  return rawCourses
    .map((course) => {
      const seen = new Set();
      const meetings = [];
      (course.meetings || []).forEach((m, idx) => {
        const sig = meetingSignature(m);
        if (seen.has(sig)) return;
        seen.add(sig);
        meetings.push({
          ...m,
          id: `${course.course_code}-${m.slot ?? idx}`,
          course_code: course.course_code,
          course_name: course.course_name,
          credits: course.credits,
          course_start_date: course.course_start_date,
          course_end_date: course.course_end_date,
        });
      });
      return {
        course_code: course.course_code,
        course_name: course.course_name,
        credits: course.credits,
        course_start_date: course.course_start_date,
        course_end_date: course.course_end_date,
        instruction_types: Array.isArray(course.instruction_types) ? course.instruction_types : [],
        meetings,
      };
    })
    .sort((a, b) => (a.course_code || '').localeCompare(b.course_code || ''));
};

export const flattenMeetings = (courses) => courses.flatMap((c) => c.meetings);

export const filterActiveMeetings = (meetings, now = new Date()) => {
  const today = startOfDay(now);
  return meetings.filter((m) => {
    const end = toLocalDate(m.end_date);
    return !end || end >= today;
  });
};

const seasonForMonth = (month) => {
  if (month >= 8) return { season: 'Fall', order: 3 };
  if (month <= 4) return { season: 'Spring', order: 1 };
  return { season: 'Summer', order: 2 };
};

const termForYearMonth = (year, month) => {
  const { season, order } = seasonForMonth(month);
  return { key: `${season} ${year}`, label: `${season} ${year}`, sort: year * 10 + order };
};

const termForDate = (dateStr) => {
  const d = toLocalDate(dateStr);
  if (!d) return null;
  return termForYearMonth(d.getFullYear(), d.getMonth() + 1);
};

// Distinct terms present in the student's courses, ordered chronologically.
export const getTerms = (courses) => {
  const map = new Map();
  courses.forEach((c) => {
    const term = termForDate(c.course_start_date);
    if (term && !map.has(term.key)) map.set(term.key, term);
  });
  return [...map.values()].sort((a, b) => a.sort - b.sort);
};

export const termKeyForCourse = (course) => termForDate(course.course_start_date)?.key ?? null;

// Term containing today, else the nearest upcoming term, else the most recent.
export const pickDefaultTerm = (terms, now = new Date()) => {
  if (!terms.length) return null;
  const todaySort = termForYearMonth(now.getFullYear(), now.getMonth() + 1).sort;
  const current = terms.find((t) => t.sort === todaySort);
  if (current) return current.key;
  const upcoming = terms.find((t) => t.sort > todaySort);
  if (upcoming) return upcoming.key;
  return terms[terms.length - 1].key;
};

// Date label for a meeting: a single date for one-off meetings, a range for
// partial-term meetings, and nothing for meetings that run the full term.
export const formatMeetingDateRange = (meeting) => {
  const start = toLocalDate(meeting.start_date);
  const end = toLocalDate(meeting.end_date);
  if (!start || !end) return null;
  if (start.getTime() === end.getTime()) return formatShortDate(start);
  const courseStart = toLocalDate(meeting.course_start_date);
  const courseEnd = toLocalDate(meeting.course_end_date);
  if (courseStart && courseEnd) {
    const DAY_MS = 86400000;
    const startsWithTerm = (start - courseStart) / DAY_MS <= 14;
    const endsWithTerm = (courseEnd - end) / DAY_MS <= 21;
    if (startsWithTerm && endsWithTerm) return null;
  }
  return `${formatShortDate(start)} – ${formatShortDate(end)}`;
};
